import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Power, RefreshCw, AlertCircle, CheckCircle2, Trash2, FileSpreadsheet, History, Copy } from 'lucide-react';
import ChamadosAtivosImportar from './ChamadosAtivosImportar';
import * as XLSX from 'xlsx';
import useTelaMobile from '../../../utils/useTelaMobile';
// ⚠️ O vocabulário dos status é COMPARTILHADO (fila, ficha do equipamento e
//    pop-up do calendário) — ver o cabeçalho do arquivo para o porquê.
import { rotuloStatus } from '../chamados-rotulos';

// =====================================================================
// Chamados — cadastro do equipamento FÍSICO de TI (o parque).
//
// É desta lista que sai o seletor de equipamento na abertura do chamado.
// Decisão (Julia, 21/08/2026): ativo físico POR LOJA, cadastrado DO ZERO — os
// 121 nomes do Auvo servem de referência, não de dado.
//
// ⚠️ A FAMÍLIA vem da API (`GET /familias`), não de uma cópia aqui. É o conserto
// preventivo do que divergiu entre back e front nos formulários de qualidade
// (registrado no CLAUDE.md): duas listas viram duas verdades, e a tela passa a
// oferecer opção que o servidor recusa.
//
// ⚠️ Equipamento com chamado no histórico NÃO se apaga — desativa. O backend
// devolve 409 nesse caso e a tela mostra a explicação dele, em vez de um erro
// genérico que pareceria bug.
// =====================================================================

const API = '/api/chamados/ativos';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const COR = 'var(--ch-cor)';
const COR_ESCURA = 'var(--ch-cor-escura)';
const COR_CLARA = 'var(--ch-cor-clara)';

const thStyle = { padding: '10px 8px', textAlign: 'left', fontSize: 12, fontWeight: 800, color: '#fff', whiteSpace: 'nowrap' };
const tdStyle = { padding: '9px 8px', fontSize: 13, color: '#334155', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' };
const inputStyle = { padding: '9px 11px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' };
const labelStyle = { display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4 };

// ⚠️⚠️ `lojas` É LISTA desde 2026-09-01 (era `loja`, uma string digitada em CAIXA
//    ALTA). Duas mudanças no mesmo campo: o cadastro passou a valer para VÁRIAS
//    lojas de uma vez, e a loja deixou de ser TEXTO LIVRE — por ele se cadastrava
//    equipamento numa loja inexistente ('FRE' por 'FER'), que desaparecia de
//    todas as telas sem erro nenhum, porque o seletor da abertura recorta por loja.
// ⚠️ `seguimento` e `local` entraram no mesmo dia e são OBRIGATÓRIOS: o formulário
//    não os pedia, e todo equipamento cadastrado à mão desde 28/08 nascia cego.
const VAZIO = {
  lojas: [], quiosque: '', familia: '', seguimento: '', local: '',
  descricao: '', numero_serie: '', numero_ativo: '',
};

const fmtData = (d) => (d ? new Date(d).toLocaleDateString('pt-BR') : '');

// Botão de ação do cartão de celular: alvo de 40px e RÓTULO visível — o ícone
// sozinho só funciona onde existe `title`, que no aparelho não aparece.
const btnCartao = (cor) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 12px', minHeight: 40,
  borderRadius: 8, border: '1px solid #e2e8f0', backgroundColor: '#fff',
  color: cor, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
});


// ⚠️⚠️ `equipe` VEM DO HUB (a faixa de fila) e recorta ESTA TELA INTEIRA: a lista,
//    as famílias oferecidas e o que é gravado. Desde 17/09/2026 o parque das duas
//    filas mora na mesma `dtbChamadoAtivo`, separado pela coluna `DEPARTAMENTO` —
//    sem o recorte, a tela de TI mostraria fritadeira e a de Manutenção, POS.
const ChamadosAtivos = ({ equipe = 'TI' }) => {
  // ⚠️ O parque é conferido NA LOJA, com o aparelho na mão (Julia, 24/08/2026):
  //    a tabela de 11 colunas não cabe num celular e vira arrasto lateral.
  const { ehMobile } = useTelaMobile();

  const [ativos, setAtivos] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [novo, setNovo] = useState(VAZIO);
  // Os quiosques (CK) da loja escolhida. ⚠️ Recarregados a cada troca de loja:
  //    oferecer o CK de outra loja penduraria o equipamento no lugar errado.
  const [quiosques, setQuiosques] = useState([]);
  // As lojas que o cadastro oferece — vêm do PARQUE, pela API. ⚠️ Não é lista no
  //    código: a grafia importa (o parque grava `ESCRITÓRIO` COM acento, e é a
  //    acentuada que a exceção de validação do escritório espera).
  const [lojasCad, setLojasCad] = useState([]);
  // As duas listas fechadas do Seguimento/Local + o de-para do local sugerido.
  // ⚠️ Vêm do servidor (`chamados-seguimentos.js`), nunca de um `DISTINCT` sobre a
  //    coluna: ela guarda a grafia CRUA da planilha, que escreve `Drive-Thru` em
  //    90 linhas e `Drive` em 48 — o seletor ofereceria o mesmo lugar duas vezes.
  const [opcoes, setOpcoes] = useState({ seguimentos: [], locais: [], localPadrao: {} });
  // Resultado do último cadastro: o que entrou e o que já existia.
  const [resultado, setResultado] = useState(null);
  // Para levar a pessoa de volta ao formulário quando ela clica em "Replicar" —
  // o botão fica na tabela, dezenas de linhas abaixo do campo que ele preenche.
  const formRef = useRef(null);
  const [salvando, setSalvando] = useState(false);
  const [busca, setBusca] = useState('');
  const [fLoja, setFLoja] = useState('');
  // Os dois filtros novos (Julia, 31/08/2026). ⚠️ `fSeguimento` guarda o valor
  // CANÔNICO, não a grafia da planilha — ver o comentário das opções, abaixo.
  const [fLocal, setFLocal] = useState('');
  const [fSeguimento, setFSeguimento] = useState('');
  // Ficha de histórico do equipamento (item pedido em 21/08/2026).
  const [fichaDe, setFichaDe] = useState(null);   // CODIGO aberto
  const [ficha, setFicha] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      // ⚠️ O `?departamento=` é o que separa as duas filas. Sem ele a rota
      //    devolve o parque inteiro — comportamento certo para uma tela de
      //    cadastro geral, e errado aqui, onde a pessoa está dentro de uma fila.
      const r = await fetch(`${API}?departamento=${encodeURIComponent(equipe)}`, { headers: auth() });
      if (!r.ok) throw new Error('Falha ao carregar os equipamentos.');
      setAtivos(await r.json());
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
    // ⚠️ `equipe` na lista: trocar de fila na faixa tem de recarregar o parque.
  }, [equipe]);

  useEffect(() => { carregar(); }, [carregar]);

  // ⚠️⚠️ O QUIOSQUE SÓ EXISTE COM UMA LOJA ESCOLHIDA, e o backend recusa a
  //    combinação (400): o CK é numerado DENTRO de cada loja, e a descrição do
  //    equipamento o nomeia ('KVS - CK1') — replicá-la para o CK2 de outra loja
  //    ficaria errado em silêncio.
  const umaLoja = novo.lojas.length === 1 ? novo.lojas[0] : '';

  useEffect(() => {
    if (!umaLoja) { setQuiosques([]); return; }
    fetch(`${API}/quiosques?loja=${encodeURIComponent(umaLoja)}`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : []))
      .then(setQuiosques)
      .catch(() => setQuiosques([]));
  }, [umaLoja]);

  useEffect(() => {
    // ⚠️⚠️ AS FAMÍLIAS SÃO DA FILA, e isso não é filtro cosmético: 'POS' não
    //    existe na Manutenção e 'Chapa' não existe no TI. Uma lista só ofereceria
    //    ao cadastro um `<select>` de 40 itens com o parque da rede inteira — e o
    //    que fosse escolhido errado só apareceria no relatório, meses depois.
    fetch(`${API}/familias?departamento=${encodeURIComponent(equipe)}`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : []))
      .then(setFamilias)
      .catch(() => setFamilias([]));
    fetch(`${API}/lojas`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : []))
      .then(setLojasCad)
      .catch(() => setLojasCad([]));
    // ⚠️ Falha aqui deixa os dois seletores VAZIOS e o cadastro travado — de
    //    propósito: inventar uma lista no front seria a segunda verdade que este
    //    arquivo evita desde a nota das famílias, no topo.
    fetch(`${API}/seguimentos`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : { seguimentos: [], locais: [], localPadrao: {} }))
      .then(setOpcoes)
      .catch(() => {});
    // ⚠️ `equipe` na lista porque as FAMÍLIAS são da fila (o `?departamento=`
    //    acima): sem ela, trocar de fila deixaria o cadastro da Manutenção
    //    oferecendo a lista de TI — família real, fila errada.
  }, [equipe]);

  /**
   * Marca/desmarca uma loja de destino.
   *
   * ⚠️ Sair de uma loja só LIMPA quiosque, série e patrimônio: os três valem para
   *    um aparelho/lugar específico, e mantê-los preenchidos faria o servidor
   *    recusar um formulário que parece certo. É a mesma limpeza que a abertura de
   *    chamado faz ao trocar de loja.
   */
  const alternarLoja = (loja) => {
    setResultado(null);
    setNovo((n) => {
      const lojas = n.lojas.includes(loja) ? n.lojas.filter((l) => l !== loja) : [...n.lojas, loja];
      const varias = lojas.length > 1;
      return {
        ...n,
        lojas,
        quiosque: varias ? '' : n.quiosque,
        numero_serie: varias ? '' : n.numero_serie,
        numero_ativo: varias ? '' : n.numero_ativo,
      };
    });
  };

  /**
   * Copia um equipamento existente para o formulário, sem as lojas.
   *
   * ⚠️⚠️ REPLICAR É ENTRADA DE TELA, NÃO ROTA: o servidor não sabe se os campos
   *    vieram de um formulário em branco ou de uma linha da tabela — é o mesmo
   *    `POST`. Uma rota `/replicar` seria a segunda cópia da mesma validação.
   * ⚠️ NÃO copia série nem patrimônio: identificam UM aparelho físico, e replicá-los
   *    gravaria o mesmo número de série em 12 lojas.
   * ⚠️ NÃO copia a loja de origem nem o quiosque: o destino é o que a pessoa vai
   *    escolher, e é a única coisa que ela precisa decidir.
   */
  const replicar = (a) => {
    setErro(''); setAviso(''); setResultado(null);
    setNovo({
      ...VAZIO,
      familia: a.FAMILIA || '',
      seguimento: a.SEGUIMENTO || '',
      local: a.LOCAL || '',
      descricao: a.DESCRICAO || '',
    });
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const cadastrar = async () => {
    setErro(''); setAviso(''); setResultado(null);
    if (!novo.lojas.length) { setErro('Escolha ao menos uma loja.'); return; }
    if (!novo.familia) { setErro('Escolha a família do equipamento.'); return; }
    if (!novo.seguimento) { setErro('Escolha o seguimento (onde o equipamento fica).'); return; }
    if (!novo.local) { setErro('Escolha o local.'); return; }
    if (!novo.descricao.trim()) { setErro('Descreva o equipamento (ex.: "POS do balcão 2").'); return; }

    setSalvando(true);
    try {
      // ⚠️⚠️ MANDA `loja` JUNTO SÓ QUANDO É UMA, e isso é a mitigação da janela do
      //    deploy (a tela nova sobe antes do backend novo, por segundos): com uma
      //    loja, o backend antigo entende o campo velho e o cadastro funciona; com
      //    várias, ele receberia `loja` indefinido e devolveria 400 — falha
      //    ALTA e visível, em vez de cadastrar em UMA das lojas escolhidas e
      //    parecer que deu certo.
      const corpo = { ...novo };
      if (novo.lojas.length === 1) corpo.loja = novo.lojas[0];
      // ⚠️ O equipamento nasce NA FILA EM QUE A PESSOA ESTÁ. O servidor também
      //    valida (a família tem de ser do departamento), então mandar o campo
      //    errado aqui vira 400 — e não equipamento na fila errada.
      corpo.departamento = equipe;

      const r = await fetch(API, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify(corpo),
      });
      const dados = await r.json();
      if (!r.ok) throw new Error(dados.message || 'Falha ao cadastrar.');

      const criados = dados.criados || [];
      const repetidos = dados.repetidos || [];
      setResultado({ criados, repetidos });
      // ⚠️ O aviso conta as DUAS coisas: "cadastrado em 3" sem dizer que 2 já
      //    existiam faria a pessoa achar que replicou em 5 lojas.
      setAviso(criados.length
        ? `Cadastrado em ${criados.length} ${criados.length === 1 ? 'loja' : 'lojas'}.`
        : 'Nada foi cadastrado — o equipamento já existia em todas as lojas escolhidas.');
      // ⚠️ Só limpa o formulário se ALGO entrou: com tudo repetido, apagar os
      //    campos obrigaria a redigitar para corrigir a descrição, que é
      //    exatamente o que a pessoa vai querer fazer.
      if (criados.length) setNovo(VAZIO);
      await carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const alternarAtivo = async (a) => {
    setErro(''); setAviso('');
    try {
      const r = await fetch(`${API}/${a.CODIGO}`, {
        method: 'PUT',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        // ⚠️ MANDA A LINHA INTEIRA, não só o `ativo`. O PUT do backend reescreve
        //    todos os campos que recebe, então campo omitido vira NULL — e
        //    desativar um equipamento apagaria em silêncio o quiosque dele.
        //    (Achado ao acrescentar o QUIOSQUE, 2026-08-24.)
        body: JSON.stringify({
          loja: a.LOJA, quiosque: a.QUIOSQUE, familia: a.FAMILIA, descricao: a.DESCRICAO,
          numero_serie: a.NUMERO_SERIE, numero_ativo: a.NUMERO_ATIVO,
          ativo: !a.ATIVO,
        }),
      });
      const dados = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(dados.message || 'Falha ao atualizar.');
      setAviso(`${a.DESCRICAO} ${a.ATIVO ? 'desativado' : 'reativado'}.`);
      await carregar();
    } catch (e) {
      setErro(e.message);
    }
  };

  const excluir = async (a) => {
    // eslint-disable-next-line no-restricted-globals -- confirmação simples; o
    // backend é quem barra de verdade quando há chamado apontando para o ativo.
    if (!window.confirm(`Apagar "${a.DESCRICAO}" (${a.CODIGO})?`)) return;
    setErro(''); setAviso('');
    try {
      const r = await fetch(`${API}/${a.CODIGO}`, { method: 'DELETE', headers: auth() });
      const dados = await r.json().catch(() => ({}));
      // ⚠️ A mensagem do 409 ENSINA o caminho (desativar). Trocá-la por um erro
      // genérico faria a trava da FK parecer defeito do sistema.
      if (!r.ok) throw new Error(dados.message || 'Falha ao excluir.');
      setAviso('Equipamento apagado.');
      await carregar();
    } catch (e) {
      setErro(e.message);
    }
  };

  // Busca que ignora acento: `NFD` separa a letra do sinal (Ó → O + ◌́) e a faixa
  // U+0300–U+036F apaga só os sinais combinantes — assim "camera" acha "Câmera".
  // ⚠️ Montado com `new RegExp` de propósito: escrito como literal, o intervalo
  //    entraria no arquivo como DOIS CARACTERES INVISÍVEIS, impossíveis de ler
  //    ou revisar. Aqui o fonte fica só com ASCII.
  const ACENTOS_COMBINANTES = new RegExp('[\u0300-\u036f]', 'g');
  const semAcento = (s) => String(s || '').normalize('NFD').replace(ACENTOS_COMBINANTES, '').toLowerCase();
  const lojas = [...new Set(ativos.map((a) => a.LOJA))].sort();

  // ---------------------------------------------------------------------
  // OS FILTROS DE LOCAL E SEGUIMENTO (Julia, 31/08/2026)
  // ---------------------------------------------------------------------
  // ⚠️⚠️ O SEGUIMENTO É FILTRADO PELO CANÔNICO, e é isso que faz o filtro servir.
  //    A coluna `SEGUIMENTO` guarda a grafia CRUA da planilha, e ela escreve a
  //    mesma coisa de duas formas: `Drive-Thru` (90 ativos) e `Drive` (48),
  //    `CK1` (24) e `CK - 1` (8). Sobre o valor cru, o seletor ofereceria o mesmo
  //    lugar DUAS VEZES, com parte dos equipamentos em cada — e quem escolhesse
  //    um acharia que a outra metade sumiu do cadastro.
  //    ⚠️ E os quatro CKs viram UM item ('CK (Dessert Center)'): o quiosque já
  //    tem coluna própria na tabela, então quem procura o CK3 filtra por
  //    quiosque — quatro linhas de CK aqui seriam ruído.
  //    ⚠️ Quem agrupa é o BACKEND (`SEGUIMENTO_FILTRO` no `GET /ativos`),
  //    não esta tela: copiar o de-para para cá criaria mais um espelho para
  //    manter, e ele muda quando a planilha muda.
  //    ⚠️ `?? SEGUIMENTO` porque a lista da ABERTURA usa a mesma rota mas pode vir
  //    de uma tela em cache antes do deploy — sem o fallback o seletor nasceria
  //    vazio, o que se parece com "o parque não tem seguimento".
  const grupoSeg = (a) => a.SEGUIMENTO_FILTRO || a.SEGUIMENTO || '';
  // ⚠️ As opções saem da lista INTEIRA, não do que já está filtrado: opção que
  //    aparece e some conforme se escolhe outra coisa faz a pessoa achar que o
  //    filtro está quebrado. O preço é poder escolher um par que dá zero — e para
  //    isso existe a mensagem de lista vazia.
  const locais = [...new Set(ativos.map((a) => a.LOCAL).filter(Boolean))].sort();
  const seguimentos = [...new Set(ativos.map(grupoSeg).filter(Boolean))].sort();

  const visiveis = ativos.filter((a) => {
    if (fLoja && a.LOJA !== fLoja) return false;
    if (fLocal && a.LOCAL !== fLocal) return false;
    if (fSeguimento && grupoSeg(a) !== fSeguimento) return false;
    if (!busca.trim()) return true;
    const alvo = semAcento(`${a.CODIGO} ${a.FAMILIA} ${a.SEGUIMENTO || ''} ${a.LOCAL || ''} ${a.DESCRICAO} ${a.NUMERO_SERIE || ''} ${a.NUMERO_ATIVO || ''}`);
    return alvo.includes(semAcento(busca));
  });

  // -------------------------------------------------------------------------
  // RELATÓRIO (.xlsx) — pedido da Julia, 21/08/2026.
  //
  // ⚠️ EXPORTA O QUE ESTÁ NA TELA, não a tabela inteira: os filtros de busca e de
  //    loja valem. Exportar tudo ignorando o filtro é a armadilha clássica — quem
  //    filtrou por uma loja e recebeu a rede inteira não confere, confia. O nome do
  //    arquivo diz o recorte, para o arquivo não mentir depois de salvo.
  // ⚠️ Os títulos das colunas são de NEGÓCIO, não do banco: quem abre a planilha
  //    não sabe o que é `QTD_ABERTOS`.
  // -------------------------------------------------------------------------
  const exportarExcel = () => {
    if (!visiveis.length) { setErro('Não há equipamentos para exportar com esse filtro.'); return; }
    const linhas = visiveis.map((a) => ({
      'Código': a.CODIGO,
      'Loja': a.LOJA,
      'Quiosque': a.QUIOSQUE || 'salão/cozinha',
      'Família': a.FAMILIA,
      'Seguimento': a.SEGUIMENTO || '',
      'Local': a.LOCAL || '',
      'Descrição': a.DESCRICAO,
      'Nº de série': a.NUMERO_SERIE || '',
      'Patrimônio': a.NUMERO_ATIVO || '',
      'Situação': a.ATIVO ? 'Em uso' : 'Desativado',
      'Chamados (total)': Number(a.QTD_CHAMADOS || 0),
      'Chamados em aberto': Number(a.QTD_ABERTOS || 0),
      'Último chamado': fmtData(a.ULTIMO_CHAMADO_EM),
      'Último atendimento': fmtData(a.ULTIMO_ATENDIMENTO_EM),
      'Último técnico': a.ULTIMO_TECNICO || '',
      // ⚠️ "Nunca teve chamado" escrito em palavras, não célula vazia: vazio numa
      //    planilha se lê como "faltou o dado", e aqui é um FATO sobre o parque.
      'Já foi atendido?': Number(a.QTD_CHAMADOS || 0) === 0
        ? 'Nunca teve chamado'
        : (a.ULTIMO_ATENDIMENTO_EM ? 'Sim' : 'Tem chamado, nenhum encerrado'),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(linhas), 'Equipamentos TI');
    // ⚠️⚠️ O NOME DO ARQUIVO CARREGA TODOS OS FILTROS, não só a loja. Este era o
    //    ponto do comentário acima ("para o arquivo não mentir depois de salvo"),
    //    e com os filtros de Local e Seguimento (31/08) uma exportação de 221
    //    itens do Kiosk sairia como "todas-as-lojas" — indistinguível, no disco,
    //    de uma exportação do parque inteiro.
    // ⚠️ Sem acento e sem espaço: o nome vai para o sistema de arquivos.
    const semEspaco = (t) => String(t).normalize('NFD').replace(ACENTOS_COMBINANTES, '').replace(/\s+/g, '-');
    const recorte = [
      fLoja || 'todas-as-lojas',
      fLocal ? semEspaco(fLocal) : null,
      fSeguimento ? semEspaco(fSeguimento) : null,
    ].filter(Boolean).map((x) => `_${x}`).join('');
    const h = new Date();
    const carimbo = `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, '0')}-${String(h.getDate()).padStart(2, '0')}`;
    XLSX.writeFile(wb, `Equipamentos_TI${recorte}_${carimbo}.xlsx`);
    setAviso(`${linhas.length} equipamento(s) exportado(s).`);
  };

  /** Abre (ou fecha) a ficha de histórico de um equipamento. */
  const abrirFicha = async (codigo) => {
    if (fichaDe === codigo) { setFichaDe(null); setFicha(null); return; }
    setFichaDe(codigo); setFicha(null); setErro('');
    try {
      const r = await fetch(`${API}/${encodeURIComponent(codigo)}/historico`, { headers: auth() });
      if (!r.ok) throw new Error('Falha ao carregar o histórico do equipamento.');
      setFicha(await r.json());
    } catch (e) {
      setErro(e.message);
    }
  };

  // =====================================================================
  // FICHA DO EQUIPAMENTO — o histórico de chamados dele.
  //
  // ⚠️ Função que devolve JSX, compartilhada pelos DOIS layouts (tabela no
  //    computador, cartão no celular). Duplicá-la faria a resposta a "este
  //    equipamento já foi atendido?" divergir conforme o aparelho.
  // =====================================================================
  const fichaDoEquipamento = () => (
    <div style={{ padding: 20, backgroundColor: '#fafafa' }}>
      {!ficha ? (
        <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>Carregando o histórico…</p>
      ) : (
        <>
          {/* ==============================================================
              OS INDICADORES DA FICHA — enxutos (Julia, 2026-08-27).

              ⚠️ Rótulos ENCURTADOS e tipo menor: os quatro números vivem lado a
                 lado num bloco que abre DENTRO de uma linha da tabela, e a
                 versão anterior usava 20px no número com "Técnicos que já
                 mexeram" ao lado — no notebook isso quebrava em duas linhas e o
                 resumo ficava mais alto que a tabela de chamados abaixo dele,
                 que é o conteúdo de verdade. Resumo é aperitivo, não prato.
              ⚠️ "Cancelados" e "Atendidos" ficam por extenso porque abreviar
                 número que se compara é onde se erra a leitura; o que encolheu
                 foi o rótulo LONGO ("Técnicos que já mexeram" → "Técnicos").
              ============================================================== */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, marginBottom: 14 }}>
            {[
              ['Chamados', ficha.resumo.total],
              ['Em aberto', ficha.resumo.emAberto],
              ['Atendidos', ficha.resumo.atendidos],
              ['Cancelados', ficha.resumo.cancelados],
            ].map(([r, v]) => (
              <div key={r}>
                <p style={{ margin: 0, fontSize: 10, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{r}</p>
                <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{v}</p>
              </div>
            ))}
            <div>
              <p style={{ margin: 0, fontSize: 10, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                Técnicos
              </p>
              <p style={{ margin: 0, fontSize: 12, color: '#334155' }}>
                {ficha.resumo.tecnicos.length ? ficha.resumo.tecnicos.join(' · ') : '—'}
              </p>
            </div>
          </div>

          {!ficha.chamados.length ? (
            // ⚠️ Frase, não tabela vazia: "nunca foi inspecionado" é a
            //    resposta à pergunta, e é uma informação boa.
            <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
              Este equipamento <strong>nunca teve chamado</strong> — nenhum técnico
              foi acionado para ele desde que entrou no cadastro.
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                <thead>
                  <tr>
                    {['#', 'Aberto em', 'Tipo', 'Assunto', 'Técnico', 'Situação', 'Encerrado em'].map((h) => (
                      <th key={h} style={{ ...thStyle, color: '#64748b', background: 'none', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ficha.chamados.map((c) => (
                    <tr key={c.ID} style={{ opacity: c.STATUS === 'Cancelado' ? 0.5 : 1 }}>
                      <td style={{ ...tdStyle, fontWeight: 800 }}>{c.ID}</td>
                      <td style={tdStyle}>{fmtData(c.ABERTO_EM)}</td>
                      <td style={{ ...tdStyle, color: '#64748b' }}>{c.TIPO_SOLICITACAO || '—'}</td>
                      <td style={tdStyle}>{c.TITULO}</td>
                      <td style={{ ...tdStyle, color: '#64748b' }}>{c.TECNICO_NOME || '—'}</td>
                      <td style={tdStyle}>{rotuloStatus(c.STATUS)}</td>
                      <td style={{ ...tdStyle, color: '#64748b' }}>{fmtData(c.ENCERRADO_EM) || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );

  // =====================================================================
  // O MESMO EQUIPAMENTO, EM CARTÃO (celular).
  //
  // ⚠️ A tabela tem 11 colunas. No celular ela não encolhe: vira outro layout, e
  //    a HIERARQUIA muda junto. Aqui manda a DESCRIÇÃO (é como a pessoa reconhece
  //    o aparelho à sua frente) e o CÓDIGO fica ao lado — é ele que se dita ao
  //    telefone, e por isso segue em monoespaçada.
  // =====================================================================
  const cartaoDoAtivo = (a) => {
    const aberta = fichaDe === a.CODIGO;
    const qtd = Number(a.QTD_CHAMADOS || 0);
    return (
      <div
        key={a.CODIGO}
        style={{
          border: '1px solid #e2e8f0', borderLeft: `4px solid ${a.ATIVO ? COR : '#cbd5e1'}`,
          borderRadius: 10, backgroundColor: '#fff', padding: 12, marginBottom: 10,
          opacity: a.ATIVO ? 1 : 0.65,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 13, color: COR }}>{a.CODIGO}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>{a.LOJA}</span>
          <span style={{ fontSize: 12, color: a.QUIOSQUE ? '#64748b' : '#cbd5e1' }}>{a.QUIOSQUE || 'salão'}</span>
          {!a.ATIVO && (
            <span style={{
              marginLeft: 'auto', padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 800,
              backgroundColor: '#f1f5f9', color: '#64748b',
            }}>
              Desativado
            </span>
          )}
        </div>

        <p style={{ margin: '0 0 2px 0', fontSize: 14, fontWeight: 700, color: '#0f172a', lineHeight: 1.35 }}>
          {a.DESCRICAO}
        </p>
        <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>{a.FAMILIA}</p>

        {(a.NUMERO_SERIE || a.NUMERO_ATIVO) && (
          <p style={{ margin: '6px 0 0 0', fontSize: 11, color: '#94a3b8' }}>
            {a.NUMERO_SERIE ? `série ${a.NUMERO_SERIE}` : ''}
            {a.NUMERO_SERIE && a.NUMERO_ATIVO ? ' · ' : ''}
            {a.NUMERO_ATIVO ? `patrimônio ${a.NUMERO_ATIVO}` : ''}
          </p>
        )}

        {/* Histórico resumido: as duas colunas que a tabela mostra à direita. */}
        <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#64748b' }}>
          {qtd === 0 ? (
            <span style={{ color: '#cbd5e1' }}>nunca atendido</span>
          ) : (
            <>
              <strong style={{ color: '#334155' }}>{qtd}</strong> chamado(s)
              {/* ⚠️ CINZA, NÃO ÂMBAR (Julia, 2026-08-27). O âmbar aqui competia com
                  o "parado há" em vermelho da fila e sugeria alerta onde não há:
                  equipamento com chamado em aberto é o estado NORMAL de quem
                  acabou de abrir — o alerta, se existir, é da fila, não do
                  cadastro. O negrito segue segurando o destaque. */}
              {Number(a.QTD_ABERTOS || 0) > 0 && (
                <span style={{ color: '#475569', fontWeight: 700 }}> · {a.QTD_ABERTOS} aberto</span>
              )}
              {a.ULTIMO_ATENDIMENTO_EM && <> · último {fmtData(a.ULTIMO_ATENDIMENTO_EM)}</>}
            </>
          )}
        </p>

        {/* ⚠️ Aqui os botões ganham RÓTULO, ao contrário da tabela. No celular não
            existe `title` para revelar o que o ícone faz — e um `Power` sem texto,
            ao lado de uma lixeira, é convite a desativar o parque sem querer. */}
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => abrirFicha(a.CODIGO)} style={btnCartao(aberta ? COR : '#64748b')}>
            <History size={14} /> {aberta ? 'Fechar' : 'Histórico'}
          </button>
          <button type="button" onClick={() => replicar(a)} style={btnCartao('#64748b')}>
            <Copy size={14} /> Replicar
          </button>
          <button type="button" onClick={() => alternarAtivo(a)} style={btnCartao('#64748b')}>
            <Power size={14} /> {a.ATIVO ? 'Desativar' : 'Reativar'}
          </button>
          <button type="button" onClick={() => excluir(a)} style={btnCartao('#94a3b8')}>
            <Trash2 size={14} /> Apagar
          </button>
        </div>

        {aberta && <div style={{ marginTop: 10 }}>{fichaDoEquipamento()}</div>}
      </div>
    );
  };

  return (
    <div>
      {/* ⚠️ O título DIZ A FILA: a mesma tela serve às duas desde 17/09/2026, e um
          "Equipamentos de TI" fixo mentiria na fila de Manutenção — logo acima de
          uma lista de fritadeiras. */}
      <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '0 0 4px 0' }}>
        Equipamentos {equipe === 'Manutencao' ? 'da Manutenção' : 'de TI'}
      </h2>
      <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 24px 0' }}>
        O parque de cada loja. É desta lista que a loja escolhe o equipamento ao abrir um chamado.
      </p>

      {erro && (
        <div style={{ display: 'flex', gap: 8, backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 12, marginBottom: 16 }}>
          <AlertCircle size={18} color="#dc2626" style={{ flexShrink: 0 }} />
          <p style={{ margin: 0, fontSize: 13, color: '#991b1b' }}>{erro}</p>
        </div>
      )}
      {aviso && (
        <div style={{ display: 'flex', gap: 8, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 12, marginBottom: 16 }}>
          <CheckCircle2 size={18} color="#16a34a" style={{ flexShrink: 0 }} />
          <p style={{ margin: 0, fontSize: 13, color: '#166534' }}>{aviso}</p>
        </div>
      )}

      {/* ⚠️ A CARGA POR PLANILHA E O PASSO A PASSO VÊM ANTES DO FORMULÁRIO de um
          por um: para quem tem dezenas de equipamentos para cadastrar (o caso da
          Manutenção), o formulário unitário é o caminho errado — e quem chega
          aqui sem saber a ordem (cadastrar → emitir → vincular) precisa lê-la
          antes de começar, não depois de já ter colado adesivo. */}
      <ChamadosAtivosImportar equipe={equipe} onGravou={carregar} isMobile={ehMobile} />

      {/* CADASTRO */}
      <div ref={formRef} style={{ padding: 18, backgroundColor: '#fafafa', borderRadius: 10, border: `1px solid ${COR_CLARA}`, marginBottom: 28 }}>
        <p style={{ margin: '0 0 14px 0', fontSize: 13, fontWeight: 800, color: '#0f172a' }}>Cadastrar equipamento</p>

        {/* -----------------------------------------------------------------
            AS LOJAS DE DESTINO (Julia, 2026-09-01)
            -----------------------------------------------------------------
            ⚠️ CHIPS e não um `<select multiple>`: são 14 opções e a escolha de
            várias é o caso novo — num select múltiplo o Ctrl+clique é invisível,
            e quem não o conhece marca uma loja por vez sem saber que pode mais.
            ⚠️ A lista vem da API (`GET /lojas`), nunca do código: a grafia do
            ESCRITÓRIO (com acento, no parque) é o que a exceção de validação do
            escritório espera. */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>
              Lojas {novo.lojas.length ? `— ${novo.lojas.length} escolhida${novo.lojas.length > 1 ? 's' : ''}` : ''}
            </label>
            {novo.lojas.length > 0 && (
              <button type="button" onClick={() => setNovo({ ...novo, lojas: [] })}
                style={{ background: 'none', border: 'none', padding: 0, fontSize: 11, fontWeight: 700, color: COR, cursor: 'pointer', fontFamily: 'inherit' }}>
                limpar
              </button>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {lojasCad.map((l) => {
              const marcada = novo.lojas.includes(l);
              return (
                <button
                  key={l} type="button" onClick={() => alternarLoja(l)}
                  style={{
                    padding: '7px 12px', minHeight: 36, borderRadius: 999, cursor: 'pointer',
                    border: `1px solid ${marcada ? COR : '#cbd5e1'}`,
                    backgroundColor: marcada ? COR : '#fff',
                    color: marcada ? '#fff' : '#475569',
                    fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
                  }}
                >
                  {l}
                </button>
              );
            })}
            {!lojasCad.length && (
              <p style={{ margin: 0, fontSize: 12, color: '#94a3b8' }}>Carregando as lojas…</p>
            )}
          </div>
          {novo.lojas.length > 1 && (
            <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#b45309' }}>
              O mesmo equipamento entra nas {novo.lojas.length} lojas. Quiosque, nº de série e
              patrimônio ficam de fora — valem para um aparelho só. Onde já existir, nada é duplicado.
            </p>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, alignItems: 'flex-end' }}>
          {/* ⚠️ O QUIOSQUE SÓ APARECE COM UMA LOJA ESCOLHIDA — e o backend recusa a
              combinação (400). Com duas lojas não existe resposta certa: o CK é
              numerado dentro de cada uma, e a descrição do equipamento o nomeia. */}
          {umaLoja && (
            <div>
              {/* ⚠️ Desabilitado (não escondido) quando a loja não tem CK: um
                  seletor vazio em loja sem quiosque vira dúvida ("faltou
                  cadastrar?"). A maioria dos equipamentos é do salão/cozinha. */}
              <label style={labelStyle} htmlFor="at-quiosque">
                Quiosque {quiosques.length ? '(opcional)' : ''}
              </label>
              <select id="at-quiosque" style={{ ...inputStyle, width: '100%' }} value={novo.quiosque}
                disabled={!quiosques.length}
                onChange={(e) => setNovo({ ...novo, quiosque: e.target.value })}>
                {/* ⚠️⚠️ O `value` é o SUFIXO ('CK 3'), que é o que se GRAVA; o texto é
                    o APELIDO ('MAL - CK L3', da coluna `apelido_ck`) — a rota
                    `/quiosques` passou a devolver os dois em 31/08/2026. Mandar o
                    apelido no `value` gravaria texto de negócio numa coluna que é
                    chave de lugar, e ele nem é único.
                    ⚠️ O código continua mostrando o sufixo na TABELA abaixo, que é
                    cadastro — aqui embaixo o eixo é o dado, não a conversa. */}
                <option value="">{quiosques.length ? 'Salão / cozinha' : 'esta loja não tem quiosque'}</option>
                {quiosques.map((q) => <option key={q.sufixo} value={q.sufixo}>{q.apelido}</option>)}
              </select>
            </div>
          )}
          <div>
            <label style={labelStyle} htmlFor="at-familia">Família</label>
            <select id="at-familia" style={{ ...inputStyle, width: '100%' }} value={novo.familia}
              onChange={(e) => setNovo({ ...novo, familia: e.target.value })}>
              <option value="">Escolha…</option>
              {familias.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          {/* -------------------------------------------------------------
              SEGUIMENTO e LOCAL (Julia, 2026-09-01) — os dois obrigatórios
              -------------------------------------------------------------
              ⚠️⚠️ O LOCAL PREENCHE SOZINHO a partir do seguimento e CONTINUA
              EDITÁVEL. Medido em 01/09: o local é determinado pelo seguimento em
              100% dos 1.812 ativos que têm os dois — duas listas independentes
              ofereceriam pares que nunca existiram (`Cozinha` + `Kiosk`).
              ⚠️ Editável, e não travado, porque o LOCAL é SNAPSHOT da planilha
              oficial, nunca regra: travar o par transformaria um espelho em regra
              de negócio, e no dia em que a planilha trouxer combinação nova o
              portal recusaria o dado real. Quem sabe o de-para é o servidor
              (`localPadrao`), não esta tela. */}
          <div>
            <label style={labelStyle} htmlFor="at-seg">Seguimento</label>
            <select id="at-seg" style={{ ...inputStyle, width: '100%' }} value={novo.seguimento}
              onChange={(e) => {
                const seg = e.target.value;
                setNovo((n) => ({ ...n, seguimento: seg, local: opcoes.localPadrao[seg] || n.local }));
              }}>
              <option value="">Escolha…</option>
              {opcoes.seguimentos.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle} htmlFor="at-local">Local</label>
            <select id="at-local" style={{ ...inputStyle, width: '100%' }} value={novo.local}
              onChange={(e) => setNovo({ ...novo, local: e.target.value })}>
              <option value="">Escolha…</option>
              {opcoes.locais.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle} htmlFor="at-desc">Descrição</label>
            <input id="at-desc" style={{ ...inputStyle, width: '100%' }} value={novo.descricao} maxLength={150}
              onChange={(e) => setNovo({ ...novo, descricao: e.target.value })} placeholder="POS do balcão 2" />
          </div>
          {/* ⚠️ Série e patrimônio identificam UM aparelho físico — somem com duas
              lojas ou mais, e o backend também os recusa ali. Replicar um número
              de série seria gravar dado falso em 12 lojas. */}
          {novo.lojas.length <= 1 && (<>
            <div>
              <label style={labelStyle} htmlFor="at-serie">Nº de série</label>
              <input id="at-serie" style={{ ...inputStyle, width: '100%' }} value={novo.numero_serie} maxLength={80}
                onChange={(e) => setNovo({ ...novo, numero_serie: e.target.value })} placeholder="opcional" />
            </div>
            <div>
              <label style={labelStyle} htmlFor="at-patrim">Patrimônio</label>
              <input id="at-patrim" style={{ ...inputStyle, width: '100%' }} value={novo.numero_ativo} maxLength={40}
                onChange={(e) => setNovo({ ...novo, numero_ativo: e.target.value })} placeholder="opcional" />
            </div>
          </>)}
          <button
            type="button" onClick={cadastrar} disabled={salvando}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '10px 18px', borderRadius: 6, border: 'none',
              background: salvando ? '#cbd5e1' : COR, color: '#fff',
              fontSize: 13, fontWeight: 700, cursor: salvando ? 'not-allowed' : 'pointer',
            }}
          >
            <Plus size={15} /> {salvando ? 'Salvando…' : 'Cadastrar'}
          </button>
        </div>
        {/* O código é gerado pelo servidor e serve para ser DITADO ao telefone —
            vale dizer isso, senão parece ruído técnico na tabela. */}
        <p style={{ margin: '12px 0 0 0', fontSize: 12, color: '#94a3b8' }}>
          O código (ex.: <code>POS-7K2P</code>) é gerado automaticamente, sem letras
          que se confundem — feito para ser ditado ao telefone.
        </p>

        {/* -----------------------------------------------------------------
            O RESULTADO, loja por loja
            -----------------------------------------------------------------
            ⚠️ Mostra o que ENTROU e o que JÁ EXISTIA, separados. Um "cadastrado
            em 3 lojas" sem a segunda lista faria a pessoa achar que replicou em 5
            — e é a duplicata pulada que ela precisa ver para decidir se muda a
            descrição do que quer cadastrar de verdade. */}
        {resultado && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #e2e8f0', display: 'grid', gap: 8 }}>
            {resultado.criados.length > 0 && (
              <p style={{ margin: 0, fontSize: 12, color: '#166534' }}>
                <strong>Cadastrado:</strong>{' '}
                {resultado.criados.map((c) => `${c.loja} (${c.codigo})`).join(' · ')}
              </p>
            )}
            {resultado.repetidos.length > 0 && (
              <p style={{ margin: 0, fontSize: 12, color: '#b45309' }}>
                <strong>Já existia — nada duplicado:</strong>{' '}
                {resultado.repetidos.map((c) => `${c.loja} (${c.codigo})`).join(' · ')}
              </p>
            )}
          </div>
        )}
      </div>

      {/* FILTROS */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input
          // ⚠️ `minWidth` fixo estoura a tela num aparelho de 360px: no celular a
          //    busca ocupa a linha inteira e cresce com o que houver.
          style={{ ...inputStyle, minWidth: 0, flex: ehMobile ? '1 1 100%' : '0 1 260px' }} value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar por código, família, descrição, série…"
        />
        <select style={inputStyle} value={fLoja} onChange={(e) => setFLoja(e.target.value)}>
          <option value="">Todas as lojas</option>
          {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        {/* ⚠️ Local antes de Seguimento porque é o recorte mais grosso (3 opções
            contra ~17), e porque é assim que a planilha os ordena. */}
        <select style={inputStyle} value={fLocal} onChange={(e) => setFLocal(e.target.value)}>
          <option value="">Todos os locais</option>
          {locais.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select style={inputStyle} value={fSeguimento} onChange={(e) => setFSeguimento(e.target.value)}>
          <option value="">Todos os seguimentos</option>
          {seguimentos.map((sg) => <option key={sg} value={sg}>{sg}</option>)}
        </select>
        <button type="button" onClick={carregar} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
          borderRadius: 6, border: `1px solid ${COR}`, backgroundColor: '#fff',
          color: COR, fontSize: 12, fontWeight: 700, cursor: 'pointer',
        }}>
          <RefreshCw size={14} /> Atualizar
        </button>
        {/* ⚠️ O rótulo diz que a exportação segue o FILTRO — quem não souber isso
            acha que baixou o parque inteiro. */}
        <button type="button" onClick={exportarExcel} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
          borderRadius: 6, border: 'none', backgroundColor: '#15803d',
          color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
        }}>
          {/* ⚠️ Diz de ONDE são os N — com três filtros, "na tela" deixou de
              informar. O rótulo é o que evita alguém exportar achando que levou o
              parque inteiro. */}
          <FileSpreadsheet size={14} /> Excel ({visiveis.length}{' '}
          {[fLoja, fLocal, fSeguimento].filter(Boolean).join(' · ') || 'na tela'})
        </button>
        <span style={{ fontSize: 12, color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>
          {visiveis.length} de {ativos.length}
        </span>
      </div>

      {carregando ? (
        <p style={{ fontSize: 14, color: '#64748b' }}>Carregando…</p>
      ) : !visiveis.length ? (
        <div style={{ padding: 40, textAlign: 'center', backgroundColor: '#fafafa', borderRadius: 12 }}>
          <p style={{ fontSize: 14, color: '#64748b', margin: 0 }}>
            {ativos.length ? 'Nenhum equipamento com esse filtro.' : 'Nenhum equipamento cadastrado ainda.'}
          </p>
        </div>
      ) : (
        ehMobile ? (
          <div>{visiveis.map(cartaoDoAtivo)}</div>
        ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
            <thead>
              <tr style={{ background: `linear-gradient(135deg, ${COR} 0%, ${COR_ESCURA} 100%)` }}>
                <th style={thStyle}>Código</th>
                <th style={thStyle}>Loja</th>
                <th style={thStyle}>Quiosque</th>
                <th style={thStyle}>Família</th>
                {/* ⚠️ SEGUIMENTO e LOCAL são SNAPSHOT da planilha oficial (pedido
                    da Julia, 28/08) — servem para conferir a carga contra a
                    origem, e não são regra: quem manda sobre onde o equipamento
                    está é Loja + Quiosque. ⚠️ Em especial, `Local = Kiosk` NÃO
                    significa Dessert Center: ele mistura os CKs com os POS41-46
                    (autoatendimento) e o POS61 (McCafé), que são do restaurante. */}
                <th style={thStyle}>Seguimento</th>
                <th style={thStyle}>Local</th>
                <th style={thStyle}>Descrição</th>
                <th style={thStyle}>Nº série</th>
                <th style={thStyle}>Patrimônio</th>
                <th style={thStyle}>Situação</th>
                {/* As duas colunas do histórico pedido: quantas vezes e quando. */}
                <th style={{ ...thStyle, textAlign: 'right' }}>Chamados</th>
                <th style={thStyle}>Último atendimento</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {visiveis.map((a) => (
                <React.Fragment key={a.CODIGO}>
                <tr style={{ opacity: a.ATIVO ? 1 : 0.55 }}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: 700 }}>{a.CODIGO}</td>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>{a.LOJA}</td>
                  <td style={{ ...tdStyle, color: a.QUIOSQUE ? '#334155' : '#cbd5e1', whiteSpace: 'nowrap' }}>
                    {a.QUIOSQUE || 'salão'}
                  </td>
                  <td style={tdStyle}>{a.FAMILIA}</td>
                  {/* ⚠️ "—" é o esperado nos 2.277 ativos carregados em 27/08: as
                      colunas nasceram depois deles, e só a próxima recarga da
                      planilha as preenche. Não é dado faltando por erro. */}
                  <td style={{ ...tdStyle, color: a.SEGUIMENTO ? '#334155' : '#cbd5e1' }}>{a.SEGUIMENTO || '—'}</td>
                  <td style={{ ...tdStyle, color: a.LOCAL ? '#334155' : '#cbd5e1' }}>{a.LOCAL || '—'}</td>
                  <td style={tdStyle}>{a.DESCRICAO}</td>
                  <td style={{ ...tdStyle, color: '#64748b' }}>{a.NUMERO_SERIE || '—'}</td>
                  <td style={{ ...tdStyle, color: '#64748b' }}>{a.NUMERO_ATIVO || '—'}</td>
                  <td style={tdStyle}>
                    <span style={{
                      display: 'inline-block', padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 800,
                      backgroundColor: a.ATIVO ? '#d1fae5' : '#f1f5f9',
                      color: a.ATIVO ? '#065f46' : '#64748b',
                    }}>
                      {a.ATIVO ? 'Em uso' : 'Desativado'}
                    </span>
                  </td>
                  {/* ⚠️ Zero é mostrado como "—", não como 0: "nunca teve chamado" é
                      diferente de "teve zero", e o traço lê mais rápido numa coluna
                      cheia de números. O aberto aparece só quando existe. */}
                  <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {Number(a.QTD_CHAMADOS || 0) === 0 ? (
                      <span style={{ color: '#cbd5e1' }}>—</span>
                    ) : (
                      <>
                        <strong>{a.QTD_CHAMADOS}</strong>
                        {/* ⚠️ Mesma cor do cartão do celular — ver o porquê lá.
                            Divergir faria o mesmo dado parecer alerta num
                            aparelho e informação no outro. */}
                        {Number(a.QTD_ABERTOS || 0) > 0 && (
                          <span style={{ color: '#475569', fontWeight: 700 }}> ({a.QTD_ABERTOS} aberto)</span>
                        )}
                      </>
                    )}
                  </td>
                  <td style={{ ...tdStyle, color: '#64748b', whiteSpace: 'nowrap' }}>
                    {a.ULTIMO_ATENDIMENTO_EM ? (
                      <>
                        {fmtData(a.ULTIMO_ATENDIMENTO_EM)}
                        {a.ULTIMO_TECNICO && (
                          <span style={{ color: '#94a3b8' }}> · {String(a.ULTIMO_TECNICO).split(' ')[0]}</span>
                        )}
                      </>
                    ) : (
                      <span style={{ color: '#cbd5e1' }}>nunca atendido</span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button type="button" onClick={() => abrirFicha(a.CODIGO)}
                      title="Histórico deste equipamento"
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: 6,
                        color: fichaDe === a.CODIGO ? COR : '#64748b',
                      }}>
                      <History size={15} />
                    </button>
                    {/* ⚠️ REPLICAR não cadastra nada sozinho: leva os campos deste
                        equipamento para o formulário lá em cima, onde a pessoa
                        escolhe as lojas de destino. Cadastrar em um clique daqui
                        seria a ação mais fácil da tela sendo também a que cria
                        13 linhas de uma vez. */}
                    <button type="button" onClick={() => replicar(a)}
                      title="Replicar este equipamento em outras lojas"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 6 }}>
                      <Copy size={15} />
                    </button>
                    <button type="button" onClick={() => alternarAtivo(a)} title={a.ATIVO ? 'Desativar' : 'Reativar'}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 6 }}>
                      <Power size={15} />
                    </button>
                    <button type="button" onClick={() => excluir(a)} title="Apagar"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 6 }}>
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>

                {/* FICHA DO EQUIPAMENTO — o histórico de chamados dele. */}
                {fichaDe === a.CODIGO && (
                  <tr>
                    <td colSpan={11} style={{ padding: 0, borderBottom: `2px solid ${COR_CLARA}` }}>
                      {fichaDoEquipamento()}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        )
      )}

      <p style={{ fontSize: 12, color: '#94a3b8', margin: '18px 0 0 0', lineHeight: 1.5 }}>
        Equipamento <strong>desativado</strong> sai da lista de abertura de chamado, mas
        continua no histórico. Equipamento com chamado registrado <strong>não pode ser
        apagado</strong> — desative-o.
      </p>
    </div>
  );
};

export default ChamadosAtivos;
